import {
    ISalesforceAddressDTO,
    ISalesforceLookupDTO,
    ISalesforceOrderDTO,
    ISalesforceOrderProductDTO,
    ISalesforceOrderRecordDTO,
} from '../../../../../api/salesforce-order.dao';
import { salesforceHtmlToPlainText } from './rich-text-to-plain-text';
import { isContractNumberVisibleForRecordType, getRecordTypeFromOrder } from './order-field-visibility';

export const EMPTY_VALUE = '—';

export type ISalesforceOrderDisplay = Record<string, string>;

const lookupName = (lookup: ISalesforceLookupDTO | null): string =>
    lookup?.name ? lookup.name : EMPTY_VALUE;

const yesNo = (value: boolean | null): string => {
    if (value === true) {
        return 'Yes';
    }
    if (value === false) {
        return 'No';
    }
    return EMPTY_VALUE;
};

const text = (value: string | number | null | undefined): string => {
    if (value == null) {
        return EMPTY_VALUE;
    }
    const rendered = String(value).trim();
    return rendered ? rendered : EMPTY_VALUE;
};

const plainText = (value: string | null | undefined): string =>
    text(salesforceHtmlToPlainText(value ?? ''));

export interface ILinkedEventSource {
    oecEventId?: string | number | null;
    oeCentralUrl?: string | number | null;
    oecUrl?: string | number | null;
    order?: ILinkedEventSource | null;
}

/** Envelope fields win, then the nested order record. `oecUrl` is the legacy name for `oeCentralUrl`. */
export const resolveLinkedEventFields = (
    order: ILinkedEventSource,
): { oecEventId: string | number | null | undefined; oeCentralUrl: string | number | null | undefined } => ({
    oecEventId: order.oecEventId ?? order.order?.oecEventId,
    oeCentralUrl: order.oeCentralUrl ?? order.oecUrl ?? order.order?.oeCentralUrl ?? order.order?.oecUrl,
});

export const linkedEventDisplayValues = (order: ISalesforceOrderDTO): Pick<ISalesforceOrderDisplay, 'oecEventId' | 'oeCentralUrl'> => {
    const fields = resolveLinkedEventFields(order);
    return {
        oecEventId: text(fields.oecEventId),
        oeCentralUrl: text(fields.oeCentralUrl),
    };
};

const lookupOrText = (value: ISalesforceLookupDTO | string | null): string => {
    if (typeof value === 'string') {
        return text(value);
    }
    return lookupName(value);
};

const addressLine = (address: ISalesforceAddressDTO | null): string => {
    if (!address) {
        return EMPTY_VALUE;
    }
    const parts = [address.street, address.city, address.state, address.postalCode, address.country]
        .filter(part => part != null && part !== '');
    return parts.length ? parts.join(', ') : EMPTY_VALUE;
};

export const mapOrderToDisplay = (order: ISalesforceOrderRecordDTO): ISalesforceOrderDisplay => {
    const showContractNumber = isContractNumberVisibleForRecordType(getRecordTypeFromOrder(order));
    return {
        // Order summary
        orderStatus: text(order.orderStatus),
        billingStatus: text(order.billingStatus),
        contractNumber: showContractNumber ? lookupOrText(order.contractNumber) : EMPTY_VALUE,
        multiEventOrder: text(order.multiEventOrder),
        parentOrder: lookupName(order.parentOrder),
        requesterContact: lookupName(order.requesterContact),
        businessLead: text(order.businessLead?.name),
        corporateRelated: lookupName(order.corporateRelated),
        opportunity: lookupName(order.opportunityName ?? null),
        channelAgencyReferral: lookupName(order.channelAgencyReferral ?? null),
        hostLob: text(order.hostLob),
        hostSubLob: text(order.hostSubLob),
        feedbackFormUrl: text(order.feedbackFormUrl),
        orderType: text(order.orderType),
        orderSubtype: text(order.orderSubtype),
        eventType: text(order.eventType ?? null),
        eventTypeClient: text(order.eventTypeClient ?? null),
        pricebook: lookupName(order.pricebook),
        cancellationFee: text(order.cancellationFee),
        cancellationReason: text(order.cancellationReason),
        cancellationOther: text(order.cancellationOther),
        syncToTeams: yesNo(order.syncToTeams),
        noStaffRequired: yesNo(order.noStaffRequired),
        hybridEvent: text(order.hybridEvent),
        recordType: lookupOrText(order.recordType),
        highProfileOrder: yesNo(order.highProfileOrder),
        highProfileReasoning: text(order.highProfileReasoning),
        highProfileAttendeeDetails: text(order.highProfileAttendeeDetails),
        accountLegalEntity: text(order.accountLegalEntity),
        isThisEventRepeatable: text(order.isThisEventRepeatable),

        // Integration
        lassoSyncStatus: text(order.lassoSyncStatus),
        lassoSyncDatetime: text(order.lassoSyncDatetime),
        teamsCreated: text(order.teamsCreated),
        processedForArchive: yesNo(order.processedForArchive),

        // Billing
        billToLegalEntity: lookupName(order.billToLegalEntity),
        billingContact: lookupName(order.billingContact),
        billingContactEmail: text(order.billingContactEmail),
        billingAddress: addressLine(order.billingAddress),
        additionalRecipient1: lookupName(order.additionalRecipient1),
        additionalRecipient1Email: text(order.additionalRecipient1Email),
        additionalRecipient2: lookupName(order.additionalRecipient2),
        additionalRecipient2Email: text(order.additionalRecipient2Email),
        billingMethod: text(order.billingMethod),
        billingConfirmation: yesNo(order.billingConfirmation),
        clientCostCode: text(order.clientCostCode),
        secondaryPO: text(order.secondaryPO),
        billingTimeSensitivity: text(order.billingTimeSensitivity),
        billingNotes: text(order.billingNotes),

        // Post-event
        eventRanSmoothly: yesNo(order.eventRanSmoothly),
        postEventNotes: plainText(order.postEventNotes),
        qaChatEnabled: text(order.qaChatEnabled),
        qaReportSent: text(order.qaReportSent),

        // Other
        description: plainText(order.description),
        oecEventId: text(order.oecEventId),
        oeCentralUrl: text(order.oeCentralUrl ?? order.oecUrl),
    };
};

export const toNumber = (value: string | number | null | undefined): number | null => {
    if (value == null || value === '') {
        return null;
    }
    const parsed = typeof value === 'number' ? value : parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
};

/** Sum of the line-item totals — drives the grand-total row and the Total Value analytics property. */
export const getProductsGrandTotal = (products: ISalesforceOrderProductDTO[]): number =>
    products.reduce((sum, product) => sum + (toNumber(product.total) ?? 0), 0);
